import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import AdminTopBar from './AdminTopBar';
import { cn } from '@/lib/utils';

interface ResponsiveAdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
}

interface ButtonLinkProps {
  to: string;
  className: string;
  children: React.ReactNode;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ to, className, children }) => (
  <Button asChild size="lg" className={`${className} w-full`}>
    <Link to={to}>
      {children}
    </Link>
  </Button>
);

const ResponsiveAdminLayout: React.FC<ResponsiveAdminLayoutProps> = ({ 
  children, 
  title = "Painel Administrativo", 
  subtitle = "Visão geral do sistema VITASANA",
  className = ""
}) => {
  const adminButtons = [
    {
      label: "Dashboard",
      to: "/admin",
      className: "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
    },
    {
      label: "Clientes",
      to: "/admin/clients",
      className: "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
    },
    {
      label: "Créditos",
      to: "/admin/creditos",
      className: "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
    },
    {
      label: "Calendário",
      to: "/admin/calendar",
      className: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
    },
    {
      label: "Reservas",
      to: "/admin/reservations",
      className: "bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600"
    },
    {
      label: "Reembolsos",
      to: "/admin/reembolsos",
      className: "bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600"
    },
    {
      label: "Salas",
      to: "/admin/rooms",
      className: "bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700"
    },
    {
      label: "Blocos de Tempo",
      to: "/admin/time-blocks",
      className: "bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
    },
    {
      label: "Dias Não Úteis",
      to: "/admin/non-working-days",
      className: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
    },
    {
      label: "Perfil",
      to: "/admin/settings",
      className: "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Vita Sana Logo" className="h-12 w-auto" />
            <h1 className="text-xl font-bold text-gray-900">Vita Sana</h1>
          </div>
          <div className="flex items-center gap-3">
            <AdminTopBar />
          </div>
        </div>
      </div>

      <div className="bg-white border-b">
        <div className="px-4 md:px-6 py-3">
          <div className="flex flex-col lg:flex-row gap-3">
            {adminButtons.map((button) => (
              <ButtonLink key={button.to} to={button.to} className={button.className}>
                {button.label}
              </ButtonLink>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
          <p className="text-gray-600">{subtitle}</p>
        </div>
        <div className={cn(className)}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveAdminLayout; 
